import {
  Row,
  Space,
  Tag,
  Tooltip,
  Spin,
  notification,
  Modal, // Import Modal from antd
} from "antd";
import { Button } from "@mui/material";
import {
  DeleteForeverRounded,
  SouthWestRounded,
  SportsScoreRounded,
  Edit, // You can choose an appropriate edit icon, such as Edit, EditOutlined, etc.
} from "@mui/icons-material";
import React, { useState } from "react";
import { Input, Checkbox } from 'antd'; // Import Input and Checkbox from antd

/**
 * Single ETF Item
 */
const EtfItem = ({
  symbol,
  item,
  updateEtf,
  removeEtf,
  toggleReplicateEtf,
  minWeightTolerance,
  contribution,
  totalWeight,
}) => {
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); // State for controlling modal visibility
  const [modalFormData, setModalFormData] = useState({
    weight: item.weight * 100,
    doNotReplicate: item.doNotReplicate,
  });

  const doNotReplicate = item.doNotReplicate;
  const isWithinTolerance = contribution >= minWeightTolerance * 0.01;
  const flagEtf = !isWithinTolerance && !doNotReplicate;

  // Some style logic
  const color = !flagEtf ? "rgba(0,0,0,0.8)" : "var(--red)";
  const fontWeight = flagEtf ? "normal" : "bold";
  const assetClass = item?.tags?.asset_class ?? "";

  let tooltipString = "";
  let background = "transparent";
  let border = "transparent";

  if (assetClass !== "equity") {
    background = "var(--warning)";
    tooltipString += `${symbol.toUpperCase()} has large concentration in asset class ${assetClass.toUpperCase()}. This may cause replication issues.`;
  }
  if (flagEtf) {
    border = "1px dashed var(--red)";
  } else if (doNotReplicate) {
    border = "1px dashed var(--primary)";
  } else if (assetClass !== "equity") {
    border = "1px dashed var(--warning-dark)";
  }

    const showModal = () => {
        setModalFormData({
            weight: item.weight * 100,
            doNotReplicate: item.doNotReplicate,
        });
        setIsModalOpen(true);
    };

    const handleModalOk = async () => {
        const sumOfOthers = totalWeight - item.weight * 100;
        const newTotal = sumOfOthers + modalFormData.weight;

        if (newTotal > 100) {
          notification.error({
            message: "Cannot exceed 100% total weight.",
            placement: "bottom",
          });
          return;
        }

        setLoading(true);
        await updateEtf(symbol, modalFormData);
        setLoading(false);
        setIsModalOpen(false);
      };

    const handleModalCancel = () => {
        setIsModalOpen(false);
    };

    const handleModalFormChange = (field, value) => {
        setModalFormData({
          ...modalFormData,
          [field]: value,
        });
      };

  return (
    <Tooltip title={tooltipString}>
      <div
        className="p-1 mb-3"
        key={symbol}
        style={{ border, background, borderRadius: "6px" }}
      >
          <Modal
              title={`Edit ${symbol}`}
              open={isModalOpen}
              onOk={handleModalOk}
              onCancel={handleModalCancel}
              okText="Save"
              cancelText="Cancel"
              confirmLoading={loading}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Input
                  addonBefore="Weight (%)"
                  type="number"
                  min={0}
                  max={100}
                  value={modalFormData.weight}
                  onChange={(e) => handleModalFormChange("weight", parseFloat(e.target.value))}
                />
                <Checkbox
                  checked={modalFormData.doNotReplicate}
                  onChange={(e) => handleModalFormChange("doNotReplicate", e.target.checked)}
                >
                  Do Not Replicate
                </Checkbox>
              </div>
          </Modal>

        <div className={'w-full'}>
          {/* Row 1: Symbol/Name (truncated) + Edit Button */}
          <Row justify="space-between" gutter={6} align="middle" className={'mb-2'}>
            <div className={'text-xs'}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "80%",
              }}
            >
              <strong>{symbol.toUpperCase()}</strong> — {item.name}
            </div>
            <Button onClick={showModal} size="small" style={{minWidth: 0, padding: '0 8px'}}>
              <Edit fontSize="small" />
            </Button>
          </Row>

          {/* Row 2: Weight & Filled % */}
          <Row justify="start" align="middle" gutter={8} className={'pl-4'}>
            <span className="text-xs">
                Weight: <strong>{(item.weight * 100).toFixed(2)}%</strong>
            </span>
            <Tag
              bordered={false}
              style={{ background: "transparent", color, fontWeight }}
            >
              Filled: {`${(contribution * 100).toFixed(2) || 0}%`}
            </Tag>
          </Row>
        </div>
      </div>
    </Tooltip>
  );
};

/**
 * Parent Controller
 */
const EtfController = ({
  etfs,
  etfContributions,
  updateEtfWeight,
  removeEtf,
  toggleReplicateEtf,
  minWeightTolerance = 0,
  sort = true,
}) => {
  // Sort items if desired
  const sortedEtfKeys = sort
    ? Object.keys(etfs).sort((a, b) => etfs[b].weight - etfs[a].weight)
    : Object.keys(etfs);

  // Compute total of all ETF weights in percentage
  const totalWeight = Object.values(etfs).reduce(
    (acc, etf) => acc + etf.weight * 100,
    0
  );

    const updateEtf = async (symbol, formData) => {
        await updateEtfWeight(symbol, formData.weight);
        await toggleReplicateEtf(symbol, formData.doNotReplicate);
  };

  return (
    <Space
      className="py-3"
      direction="vertical"
      style={{ width: "100%", overflowX: "scroll" }}
    >
      <Row justify="space-between" align="middle">
        <span className={'text-default font-medium uppercase mb-3'}>
          ETFs Used in SMA
        </span>
        {/* You can add any global actions related to ETFs here, if needed */}
      </Row>
      {sortedEtfKeys.map((symbol) => (
        <EtfItem
          key={symbol}
          symbol={symbol}
          item={etfs[symbol]}
          contribution={etfContributions[symbol]}
          updateEtf={updateEtf}
          removeEtf={removeEtf}
          toggleReplicateEtf={toggleReplicateEtf}
          minWeightTolerance={minWeightTolerance}
          totalWeight={totalWeight}
        />
      ))}
    </Space>
  );
};

export default EtfController;